import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './index.css';
import { Form } from '@formio/react';
import Loader from '../../../components/common/Loader';
import { Paper } from '../../../components/common/Containers';
import Typography from '../../../components/common/Typography';
import { Button } from '../../../components/common/Buttons';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useTrans } from '../../../services/i18n';
import connect from '../connect';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Box from '@material-ui/core/Box';

const RESPONSE_ID = 'formsAssignment';
const FormDetail = ({ getFormStackById, formSubmission, formPartialSubmission, downloadFormPdf, org }) => {
  const { id } = useParams();
  const { successAlert, errorAlert } = useAlertContext();
  const { trans } = useTrans();
  const history = useHistory();

  const [formDef, setFormDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partialData, setPartialData] = useState({});
  const [currentFormData, setCurrentFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingPartial, setIsSavingPartial] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isFormReady, setIsFormReady] = useState(false);
  const isInitialMountRef = React.useRef(true);

  const findComponentByKey = (components, targetKey) => {
    if (!Array.isArray(components)) return null;

    for (const component of components) {
      if (component.key === targetKey) return component;

      if (component.components) {
        const found = findComponentByKey(component.components, targetKey);
        if (found) return found;
      }

      if (component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            const found = findComponentByKey(column.components, targetKey);
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  const collectFormFieldKeys = (components, depth = 0) => {
    const keys = [];
    if (!Array.isArray(components)) return keys;

    for (const component of components) {
      if (
        component.key &&
        component.input !== false &&
        component.type !== 'button' &&
        component.type !== 'content' &&
        component.type !== 'htmlelement'
      ) {
        keys.push(component.key);
      }

      if (component.type === 'columns' && component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            keys.push(...collectFormFieldKeys(column.components, depth + 1));
          }
        }
      } else if (component.type === 'fieldset' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      } else if (component.type === 'panel' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      } else if (component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      }
    }
    return keys;
  };

  const prepareFormSubmissionData = (formData = {}) => {
    const orderedData = {};

    if (formDef?.components) {
      const fieldKeys = collectFormFieldKeys(formDef.components);

      for (const key of fieldKeys) {
        if (formData.hasOwnProperty(key)) {
          orderedData[key] = formData[key];
        }
      }

      for (const [key, value] of Object.entries(formData)) {
        if (!orderedData.hasOwnProperty(key)) {
          orderedData[key] = value;
        }
      }
    } else {
      Object.assign(orderedData, formData);
    }

    if (org?.associatedSalesforceId) {
      orderedData.accountId = org.associatedSalesforceId;
    }

    if (formDef?.components) {
      const formTypeComponent = findComponentByKey(formDef.components, 'formType');
      if (formTypeComponent?.defaultValue) {
        orderedData.formType = formTypeComponent.defaultValue;
      }
    }

    return orderedData;
  };

  const onPartial = () => {
    if (isSavingPartial) return;

    setIsSavingPartial(true);
    const enhancedPartialData = prepareFormSubmissionData(partialData);
    formPartialSubmission({
      formType: 'principal',
      formUsfcrId: formDef.formUsfcrId,
      formStackId: formDef._id,
      currentSection: '',
      fields: enhancedPartialData,
    })
      .then(() => {
        successAlert(trans('form-progress-saved'));
        history.push('/client-portal-forms');
      })
      .catch(() => {
        errorAlert(trans('error-saving-form'));
        setIsSavingPartial(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    setIsFormReady(false);
    isInitialMountRef.current = true;

    getFormStackById({ id, formType: 'principal' })
      .then((res) => {
        setFormDef(res.payload.form);
        const savedPartialData = res.payload.form.partialSubmission || {};

        setPartialData(savedPartialData);
        setCurrentFormData(savedPartialData);
        setFormKey((prev) => prev + 1);
        setIsFormReady(true);
      })
      .catch(() => {
        errorAlert(trans('error-loading-form'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, getFormStackById, errorAlert, trans]);

  if (loading || !isFormReady) {
    return <Loader open />;
  }
  if (!formDef) {
    return <Typography>{trans('form-not-found')}</Typography>;
  }

  return (
    <>
      <Loader open={isSubmitting || isSavingPartial} />
      <Paper style={{ padding: 24 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push('/client-portal-forms')}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography variant="h6" style={{ marginLeft: 8 }}>
              {trans('cancel-and-quit')}
            </Typography>
          </Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {formDef.title}
          </Typography>

          <Button variant="contained" color="info" onClick={onPartial} style={{ minWidth: 160 }}>
            {trans('save-and-resume-later')}
          </Button>
        </Box>
        <div className="my-form-wrapper">
          <Form
            key={`form-${formDef._id}-${formKey}`}
            form={formDef}
            submission={{ data: currentFormData }}
            options={{
              disableAlerts: false,
            }}
            onChange={({ data }) => {
              // Ignore first onChange from FormIO initialization to preserve loaded data
              if (isInitialMountRef.current) {
                isInitialMountRef.current = false;
                return;
              }

              setPartialData(data);
              setCurrentFormData(data);
            }}
            onSubmit={({ data }) => {
              if (isSubmitting) return Promise.resolve();

              setIsSubmitting(true);

              const mergedData = { ...currentFormData, ...data };
              const enhancedSubmissionData = prepareFormSubmissionData(mergedData);

              return formSubmission({
                formFields: enhancedSubmissionData,
                usfcr_form_id: formDef._id,
                formUsfcrId: formDef.formUsfcrId,
                type: 'principal',
              })
                .then((res) => {
                  if (res?.payload?.assignment) {
                    localStorage.setItem(RESPONSE_ID, JSON.stringify(res?.payload?.assignment));
                  }
                  successAlert(trans('form-submitted'));
                  history.push('/client-portal-forms');
                })
                .catch(() => {
                  errorAlert(trans('error-submitting-form'));
                  setIsSubmitting(false);
                });
            }}
            onError={() => {
              errorAlert(trans('error-submitting-form'));
            }}
          />
        </div>
      </Paper>
    </>
  );
};

export default connect(FormDetail);
